import { useState, useEffect, useCallback } from 'react';
import SEO, { PAGE_SEO } from '../components/SEO';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Search, Package, ExternalLink, Sparkles, Droplet, Sun, Scissors, Heart, Leaf, User, Smile, FlaskRound } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  sparkles: Sparkles,
  smile: Smile,
  droplet: Droplet,
  flask: FlaskRound,
  sun: Sun,
  scissors: Scissors,
  heart: Heart,
  palette: Sparkles,
  user: User,
  leaf: Leaf
};

export default function Products() {
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [categoriesRes, featuredRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/products/categories`),
          fetch(`${BACKEND_URL}/api/products/featured`)
        ]);

        if (categoriesRes.ok) setCategories(await categoriesRes.json());
        if (featuredRes.ok) setFeaturedProducts(await featuredRes.json());
      } catch (error) {
        console.error('Initial fetch error:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Search products
  const searchProducts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setProducts([]);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/products/search?q=${encodeURIComponent(query)}&limit=24`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
        if (data.length === 0) {
          toast.info('No products found. Try a different search term.');
        }
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search products');
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle search submit
  const handleSearch = (e) => {
    e.preventDefault();
    searchProducts(searchQuery);
    setSelectedCategory(null);
  };

  // Handle category click
  const handleCategoryClick = (category) => {
    setSelectedCategory(category.name);
    setSearchQuery(category.query);
    searchProducts(category.query);
  };

  const displayProducts = products.length > 0 ? products : featuredProducts;

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="products-page">
      <SEO {...PAGE_SEO.products} url="/products" />
      <Navbar />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/10 rounded-sm mb-4">
              <Package className="w-4 h-4 text-gold" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gold">Open Beauty Facts</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold mb-4">Discover Products</h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Search over 100,000 real beauty products from the Open Beauty Facts database.
            </p>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-8">
            <div className="relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search for skincare, haircare, makeup..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-24 h-14 text-lg rounded-sm search-input"
                data-testid="product-search-input"
              />
              <Button 
                type="submit" 
                className="absolute right-2 top-1/2 -translate-y-1/2 btn-gold h-10"
                disabled={loading}
                data-testid="product-search-btn"
              >
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </form>

          {/* Categories */}
          <div className="mb-12">
            <h2 className="font-serif text-xl font-semibold mb-4">Browse by Category</h2>
            <div className="flex flex-wrap gap-3">
              {initialLoading ? (
                [...Array(8)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-28" />
                ))
              ) : (
                categories.map((category) => {
                  const IconComponent = categoryIcons[category.icon] || Sparkles;
                  return (
                    <Button
                      key={category.name}
                      variant={selectedCategory === category.name ? "default" : "outline"}
                      onClick={() => handleCategoryClick(category)}
                      className={`gap-2 ${selectedCategory === category.name ? 'bg-gold text-black hover:bg-gold/90' : ''}`}
                      data-testid={`category-${category.name.toLowerCase()}`}
                    >
                      <IconComponent className="w-4 h-4" />
                      {category.name}
                    </Button>
                  );
                })
              )}
            </div>
          </div>

          {/* Results Header */}
          {(products.length > 0 || selectedCategory) && (
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-xl font-semibold">
                {selectedCategory ? `${selectedCategory} Products` : 'Search Results'}
                <span className="ml-2 text-sm text-muted-foreground font-normal">
                  ({products.length} found)
                </span>
              </h2>
              {selectedCategory && (
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setSelectedCategory(null);
                    setProducts([]);
                    setSearchQuery('');
                  }}
                >
                  Clear Filter
                </Button>
              )}
            </div>
          )}

          {/* Products Grid */}
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <Card key={i} className="border-none">
                  <Skeleton className="aspect-square" />
                  <CardContent className="p-4">
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-5 w-full mb-2" />
                    <Skeleton className="h-4 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : displayProducts.length === 0 && !initialLoading ? (
            <div className="text-center py-16">
              <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-serif text-xl font-semibold mb-2">No Products Found</h3>
              <p className="text-muted-foreground mb-6">
                Try searching for something like "vitamin c serum" or "moisturizer"
              </p>
            </div>
          ) : (
            <>
              {!products.length && !selectedCategory && featuredProducts.length > 0 && (
                <h2 className="font-serif text-xl font-semibold mb-6">Featured Products</h2>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {displayProducts.map((product, index) => (
                  <Card 
                    key={`${product.product_id}-${index}`} 
                    className="bg-card border-none shadow-card card-hover overflow-hidden group"
                    data-testid={`product-${product.product_id}`}
                  >
                    <div className="aspect-square overflow-hidden bg-secondary">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="w-full h-full items-center justify-center hidden">
                        <Package className="w-16 h-16 text-muted-foreground" />
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gold mb-1 truncate">
                        {product.brand || 'Unknown Brand'}
                      </p>
                      <h3 className="font-serif font-semibold mb-2 line-clamp-2 group-hover:text-gold transition-colors">
                        {product.name || 'Unknown Product'}
                      </h3>
                      {product.categories && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {product.categories.split(',').slice(0, 2).map((cat, i) => (
                            <Badge key={i} variant="secondary" className="text-xs truncate max-w-[100px]">
                              {cat.trim()}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <a 
                        href={`https://world.openbeautyfacts.org/product/${product.product_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-gold hover:underline"
                      >
                        View Details
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}

          {/* Info Card */}
          <Card className="bg-secondary/50 border-none mt-12" data-testid="open-beauty-facts-info">
            <CardContent className="p-8 text-center">
              <Package className="w-12 h-12 text-gold mx-auto mb-4" />
              <h3 className="font-serif text-xl font-semibold mb-2">Powered by Open Beauty Facts</h3>
              <p className="text-muted-foreground max-w-2xl mx-auto mb-4">
                Open Beauty Facts is a free, open, collaborative database of cosmetic products from around the world.
                Data is crowdsourced by a non-profit organization with over 100,000 products from 170 countries.
              </p>
              <a 
                href="https://world.openbeautyfacts.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-gold hover:underline"
              >
                Learn More About Open Beauty Facts
                <ExternalLink className="w-4 h-4" />
              </a>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
